import { FormattedMessage } from "react-intl";
import classNames from "classnames/bind";
import { Amount } from "app/components/ui/Amount/Amount";
import { Button } from "app/components/ui/Button/Button";
import { BadgeItem } from "app/components/ui/badge/BadgeItem";
import { ArrowRightIcon } from "app/icons/navigation";
import { CardLink, CardLinkProps } from "app/components/ui/CardLink/CardLink";
import { Image } from "app/components/ui/Image/Image";
import {
	CardContent,
	CardDescription,
	CardFooter,
	CardMedia,
	CardTitle,
} from "app/components/ui/Card/Card";
import styles from "./MerchCard.module.scss";

const cx = classNames.bind(styles);

const IMAGE_SIZES = "(min-width: 768px) 404px, 265px";

export type MerchCardPhoto = { url: string; thumbnail?: string };

export type MerchCardProps = {
	/** Sanitized HTML title, resolved by the caller. */
	title: string;
	/** Sanitized HTML subtitle, resolved by the caller. */
	subtitle?: string;
	photo?: MerchCardPhoto;
	/** Shown when greater than 0; otherwise the "see offers" CTA label is displayed instead. */
	price?: number;
	/** Shows the "price drop" badge over the image. */
	showPriceDrop?: boolean;
} & Omit<CardLinkProps, "variant" | "children" | "title">;

export const MerchCard = ({
	title,
	subtitle,
	photo,
	price,
	showPriceDrop = false,
	...rest
}: MerchCardProps) => {
	const showPrice = (price ?? 0) > 0;

	return (
		<CardLink variant="elevated" {...rest}>
			<CardMedia className={cx("merch-card__media")}>
				{showPriceDrop && (
					<div className={cx("merch-card__badge")}>
						<BadgeItem type="priceDrop" />
					</div>
				)}

				{photo?.url && (
					<Image
						fill
						src={photo.url}
						alt=""
						width={232}
						height={220}
						sizes={IMAGE_SIZES}
						thumbnail={photo.thumbnail}
					/>
				)}
			</CardMedia>

			<CardContent className={cx("merch-card__content")}>
				<CardTitle className={cx("merch-card__title")} data-testid="merch-card-title">
					<span dangerouslySetInnerHTML={{ __html: title }} />
				</CardTitle>

				{subtitle && (
					<CardDescription
						className={cx("merch-card__intro")}
						data-testid="merch-card-subtitle"
					>
						<span dangerouslySetInnerHTML={{ __html: subtitle }} />
					</CardDescription>
				)}
			</CardContent>

			<CardFooter className={cx("merch-card__footer")}>
				{showPrice && <Amount value={price ?? 0} from perPerson data-testid="amount" />}

				<Button
					asChild
					variant="primary"
					size={showPrice ? "icon-sm" : "sm"}
					fullWidth={!showPrice}
					endIcon={<ArrowRightIcon />}
				>
					<span aria-hidden="true" className={cx("merch-card__cta")}>
						{!showPrice && <FormattedMessage id="current.merch.collection.cta.label" />}
					</span>
				</Button>
			</CardFooter>
		</CardLink>
	);
};
